/**
 * The report as a web page: read-only, page-shaped, and interactive where a PDF
 * cannot be.
 *
 * This is the other half of the product. A saved template plus data is a file,
 * and it is also something a person should be able to *read* — in a tab, on a
 * phone, shared by link — without downloading anything. The drawing is
 * identical either way: the same paginator decides the breaks, the same blocks
 * draw the marks, the same SVG the PDF contains is the SVG the browser shows.
 * What differs is only what a screen can do that paper cannot, which is why
 * chart interactions are on by default here and stripped on the way to the file.
 *
 * It is deliberately a thin layer over `createReportPreview`. Everything about
 * measurement, pagination and rendering is that pipeline's job and must stay
 * there, or the viewer and the file start to disagree — which is the one thing
 * this product exists to prevent. What is added here is presentation and
 * navigation: paper on a ground, a fit-to-width zoom that responds to the
 * element's own width, page navigation, and an optional toolbar.
 */
import { enableChartInteractions, type ChartInteractionOptions } from "./chart-interactions.js";
import { createReportPreview, type PreviewOptions, type ReportPreview } from "./preview.js";
import { ensureStyle } from "./dom.js";
import type { LayoutResult } from "./layout.js";

/** How the viewer sizes the page against the space it has. */
export type ViewerFit = "width" | "page" | "actual";

export interface ReportViewerOptions extends Omit<PreviewOptions, "mode" | "zoom"> {
  /**
   * How to size pages. `"width"` (the default) fits the page to the element,
   * never enlarging past 100% — a report blown up to 180% on a wide monitor
   * looks like a mistake, and the text is already sized for print. `"page"`
   * fits a whole page in view; `"actual"` pins it to 100%.
   */
  fit?: ViewerFit;
  /** A fixed zoom, which overrides `fit`. 1 is actual size. */
  zoom?: number;
  /** Gap between pages, in CSS px at 100%. Default 32. */
  pageGap?: number;
  /**
   * The toolbar: page position, zoom and print. `false` hides it, for a host
   * that would rather draw its own chrome around `goToPage` and `setZoom`.
   * Default true.
   */
  toolbar?: boolean;
  /**
   * Pointing at a chart mark shows what it stands for. Default true — it is
   * most of the reason to read a report on a screen rather than on paper.
   * `false`, or options passed straight to `enableChartInteractions`.
   */
  interactions?: boolean | ChartInteractionOptions;
  /** Extra class on the viewer root. */
  className?: string;
  /** Accessible label for the scroll region. Default "Report". */
  label?: string;
  /** Called whenever the visible page changes. */
  onPageChange?(page: number): void;
  /** Called after each layout, with the result. */
  onLayout?(result: LayoutResult): void;
  /**
   * What the toolbar's print button does. The default opens the browser's
   * print dialog on the page as laid out, which produces a file matching this
   * view. Supply your own to hand the reader a real PDF instead.
   */
  onPrint?(result: LayoutResult): void;
}

export interface ReportViewer {
  /** Re-renders with new input. Resolves with the new layout. */
  update(input: Partial<ReportViewerOptions>): Promise<LayoutResult>;
  /** The last layout result, or null before the first render. */
  readonly result: LayoutResult | null;
  /** The page currently in view, 1-based. */
  readonly page: number;
  /** The number of pages, or 0 before the first render. */
  readonly pageCount: number;
  /** Scrolls to a page, 1-based and clamped. */
  goToPage(page: number): void;
  /** Sets an explicit zoom, or returns to a fit mode. */
  setZoom(zoom: number | ViewerFit): void;
  /** The zoom actually in use, resolved from the fit mode. */
  readonly zoom: number;
  /** Opens the print dialog (or calls `onPrint`). */
  print(): void;
  destroy(): void;
}

/**
 * Viewer chrome, kept out of `DOCUMENT_CSS` on purpose.
 *
 * That stylesheet is shared with the PDF path — the Chromium backend prints the
 * very markup it styles — so a rule added there for a scrollbar or a toolbar is
 * a rule that can reach the file. These are scoped under `.bp-viewer`, which
 * wraps the document rather than being part of it, and none of it is inside
 * `.bp-page`.
 */
const VIEWER_CSS = `
.bp-viewer { display: flex; flex-direction: column; width: 100%; height: 100%; min-height: 0; background: #f1f2f4; color: #1a1d21; font: 13px/1.4 system-ui, -apple-system, "Segoe UI", sans-serif; }
.bp-viewer *, .bp-viewer *::before, .bp-viewer *::after { box-sizing: border-box; }
.bp-viewer__scroll { flex: 1 1 auto; min-height: 0; overflow: auto; padding: 24px 16px; scroll-behavior: smooth; }
.bp-viewer__stage { margin: 0 auto; transform-origin: top left; }
.bp-viewer .bp-preview__page { box-shadow: 0 1px 3px rgba(16,24,40,0.14), 0 10px 28px rgba(16,24,40,0.10); }

.bp-viewer__bar { flex: 0 0 auto; display: flex; align-items: center; gap: 8px; padding: 6px 10px; background: #fff; border-bottom: 1px solid #e2e5ea; }
.bp-viewer__spacer { flex: 1 1 auto; }
.bp-viewer__btn { display: inline-flex; align-items: center; justify-content: center; min-width: 28px; height: 28px; padding: 0 8px; border: 1px solid #e2e5ea; border-radius: 6px; background: #fff; color: inherit; font: inherit; cursor: pointer; }
.bp-viewer__btn:hover:not(:disabled) { background: #f5f6f8; }
.bp-viewer__btn:disabled { opacity: 0.4; cursor: default; }
.bp-viewer__btn:focus-visible, .bp-viewer__field:focus-visible { outline: 2px solid #4a6fa5; outline-offset: 1px; }
.bp-viewer__count { font-variant-numeric: tabular-nums; white-space: nowrap; }
.bp-viewer__field { width: 3.5em; height: 28px; padding: 0 6px; border: 1px solid #e2e5ea; border-radius: 6px; font: inherit; text-align: right; font-variant-numeric: tabular-nums; }

/* The reader's own print of the view. The page boxes are already exact, so all
   this has to do is get the ground and the chrome out of the way. */
@media print {
  .bp-viewer { background: #fff; height: auto; }
  .bp-viewer__bar { display: none; }
  .bp-viewer__scroll { overflow: visible; padding: 0; }
  .bp-viewer__stage { transform: none !important; width: auto !important; }
  .bp-viewer .bp-preview__page { box-shadow: none; margin: 0 !important; break-after: page; page-break-after: always; }
  .bp-viewer .bp-preview__page:last-child { break-after: auto; page-break-after: auto; }
}

@media (prefers-color-scheme: dark) {
  .bp-viewer { background: #22252a; color: #e8eaed; }
  .bp-viewer__bar { background: #2b2f36; border-bottom-color: #3a3f47; }
  .bp-viewer__btn, .bp-viewer__field { background: #2b2f36; border-color: #3a3f47; color: inherit; }
  .bp-viewer__btn:hover:not(:disabled) { background: #343941; }
}
`;

/** Clamps to a range, tolerating a NaN low bound of an empty document. */
const clamp = (n: number, lo: number, hi: number) => (n < lo ? lo : n > hi ? hi : n);

function button(doc: Document, label: string, title: string): HTMLButtonElement {
  const b = doc.createElement("button");
  b.type = "button";
  b.className = "bp-viewer__btn";
  b.textContent = label;
  b.title = title;
  b.setAttribute("aria-label", title);
  return b;
}

/**
 * Mounts a read-only, page-shaped view of a template into an element.
 *
 * The container is expected to have a height — the viewer fills it and scrolls
 * inside itself, so that the toolbar stays put and `fit: "width"` has something
 * to measure against. A container with no height collapses to the toolbar.
 */
export function createReportViewer(container: HTMLElement, options: ReportViewerOptions): ReportViewer {
  const doc = container.ownerDocument;
  const win = doc.defaultView;
  ensureStyle(doc, "bp-viewer-css", VIEWER_CSS);

  let opts: ReportViewerOptions = { ...options };
  let destroyed = false;
  let page = 1;
  let zoom = 1;
  let stopInteractions: (() => void) | null = null;

  const root = doc.createElement("div");
  root.className = `bp-viewer ${opts.className ?? ""}`.trim();

  const bar = doc.createElement("div");
  bar.className = "bp-viewer__bar";
  bar.setAttribute("role", "toolbar");
  bar.setAttribute("aria-label", "Report controls");

  const prev = button(doc, "‹", "Previous page");
  const next = button(doc, "›", "Next page");
  const pageField = doc.createElement("input");
  pageField.className = "bp-viewer__field";
  pageField.type = "text";
  pageField.inputMode = "numeric";
  pageField.setAttribute("aria-label", "Page number");
  const count = doc.createElement("span");
  count.className = "bp-viewer__count";
  const zoomOut = button(doc, "−", "Zoom out");
  const zoomIn = button(doc, "+", "Zoom in");
  const zoomLabel = doc.createElement("button");
  zoomLabel.type = "button";
  zoomLabel.className = "bp-viewer__btn bp-viewer__count";
  zoomLabel.title = "Fit to width";
  const printBtn = button(doc, "Print", "Print or save as PDF");
  const spacer = doc.createElement("span");
  spacer.className = "bp-viewer__spacer";

  bar.append(prev, pageField, count, next, spacer, zoomOut, zoomLabel, zoomIn, printBtn);

  const scroll = doc.createElement("div");
  scroll.className = "bp-viewer__scroll";
  scroll.tabIndex = 0;
  scroll.setAttribute("role", "region");
  scroll.setAttribute("aria-label", opts.label ?? "Report");

  const stage = doc.createElement("div");
  stage.className = "bp-viewer__stage";
  scroll.appendChild(stage);

  if (opts.toolbar !== false) root.appendChild(bar);
  root.appendChild(scroll);
  container.appendChild(root);

  // Read-only throughout: `mode: "preview"` is what resolves real data and drops
  // every design-mode affordance. A viewer showing dashed drop targets and
  // "sample data" badges would be showing the reader the editor's furniture.
  const preview: ReportPreview = createReportPreview(stage, { ...opts, mode: "preview", zoom: 1 });

  /** The zoom a fit mode works out to, given the space there actually is. */
  function fitZoom(): number {
    const result = preview.result;
    if (!result) return 1;
    const explicit = opts.zoom;
    if (typeof explicit === "number") return explicit;
    const fit = opts.fit ?? "width";
    if (fit === "actual") return 1;
    // The padding is the scroll box's own, and the page cannot use it.
    const styles = win?.getComputedStyle(scroll);
    const padX = styles ? parseFloat(styles.paddingLeft || "0") + parseFloat(styles.paddingRight || "0") : 32;
    const padY = styles ? parseFloat(styles.paddingTop || "0") + parseFloat(styles.paddingBottom || "0") : 48;
    const availableWidth = scroll.clientWidth - padX;
    if (!(availableWidth > 0)) return 1;
    const byWidth = availableWidth / result.page.width;
    if (fit === "width") {
      // Never past 100%: the type is already sized for paper, and enlarging it
      // on a wide monitor reads as a rendering fault rather than as a feature.
      return clamp(byWidth, 0.1, 1);
    }
    const availableHeight = scroll.clientHeight - padY;
    if (!(availableHeight > 0)) return clamp(byWidth, 0.1, 1);
    return clamp(Math.min(byWidth, availableHeight / result.page.height), 0.1, 1);
  }

  function applyZoom(): void {
    zoom = fitZoom();
    const result = preview.result;
    stage.style.transform = zoom === 1 ? "" : `scale(${zoom})`;
    if (result) {
      // A transform is painting, not layout: a scaled element still occupies
      // its unscaled box. Left alone, a page shrunk to fit still reserved its
      // full width and height, so the scroll box kept a page's worth of empty
      // space below the document and scrolled sideways past its right edge.
      //
      // The width is given as the scaled width and the origin is the top left
      // corner, so the box and the paint agree and `margin: 0 auto` still
      // centres what the reader sees. The children stay full width and hang
      // out of that box, which is exactly what the scale then pulls back in.
      const unscaled = stage.scrollHeight;
      stage.style.width = `${Math.round(result.page.width * zoom)}px`;
      stage.style.marginBottom = zoom < 1 ? `${-Math.round(unscaled * (1 - zoom))}px` : "";
    }
    zoomLabel.textContent = `${Math.round(zoom * 100)}%`;
    zoomLabel.setAttribute("aria-label", `Zoom, ${Math.round(zoom * 100)} percent. Activate to fit to width.`);
  }

  /** The page whose top is nearest the top of the scroll box. */
  function visiblePage(): number {
    const pages = stage.querySelectorAll<HTMLElement>("[data-page]");
    if (!pages.length) return 1;
    const top = scroll.getBoundingClientRect().top;
    let best = 1;
    let bestDistance = Infinity;
    for (const el of pages) {
      const box = el.getBoundingClientRect();
      // Bottom-of-page beats top-of-page: a page still half on screen is the
      // one being read, not the one whose first line has just appeared.
      const distance = box.bottom < top ? Infinity : Math.abs(box.top - top);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = Number(el.getAttribute("data-page")) || 1;
      }
    }
    return best;
  }

  function syncBar(): void {
    const total = preview.result?.paged.totalPages ?? 0;
    count.textContent = total ? `/ ${total}` : "";
    if (doc.activeElement !== pageField) pageField.value = total ? String(page) : "";
    pageField.disabled = total < 2;
    prev.disabled = page <= 1;
    next.disabled = total === 0 || page >= total;
  }

  function setPage(next_: number, scrollTo: boolean): void {
    const total = preview.result?.paged.totalPages ?? 0;
    const wanted = total ? clamp(Math.round(next_), 1, total) : 1;
    if (scrollTo) {
      const el = stage.querySelector<HTMLElement>(`[data-page="${wanted}"]`);
      // Measured from rectangles rather than `offsetTop`, for two reasons: the
      // stage is scaled, and a rectangle is already in scaled coordinates while
      // `offsetTop` is not; and `offsetParent` is whatever positioned ancestor
      // the *host* happens to have, which is not ours to assume.
      if (el) scroll.scrollTop += el.getBoundingClientRect().top - scroll.getBoundingClientRect().top;
    }
    if (wanted === page) return;
    page = wanted;
    syncBar();
    opts.onPageChange?.(page);
  }

  const onScroll = () => {
    const seen = visiblePage();
    if (seen === page) return;
    page = seen;
    syncBar();
    opts.onPageChange?.(page);
  };
  scroll.addEventListener("scroll", onScroll, { passive: true });

  const onKey = (e: KeyboardEvent) => {
    if (e.target === pageField) return;
    if (e.key === "PageDown") setPage(page + 1, true);
    else if (e.key === "PageUp") setPage(page - 1, true);
    else if (e.key === "Home") setPage(1, true);
    else if (e.key === "End") setPage(preview.result?.paged.totalPages ?? 1, true);
    else return;
    e.preventDefault();
  };
  scroll.addEventListener("keydown", onKey);

  prev.addEventListener("click", () => setPage(page - 1, true));
  next.addEventListener("click", () => setPage(page + 1, true));
  pageField.addEventListener("change", () => setPage(Number(pageField.value), true));
  zoomLabel.addEventListener("click", () => api.setZoom("width"));
  zoomIn.addEventListener("click", () => api.setZoom(clamp(zoom + 0.1, 0.1, 4)));
  zoomOut.addEventListener("click", () => api.setZoom(clamp(zoom - 0.1, 0.1, 4)));
  printBtn.addEventListener("click", () => api.print());

  // Fit is against the element, never the window: the viewer is embedded, so a
  // narrow pane in a wide page and a phone are the same problem and the window
  // answers neither. Same reasoning as the designer's own narrow layout.
  const observer = win && "ResizeObserver" in win ? new win.ResizeObserver(() => applyZoom()) : null;
  observer?.observe(scroll);

  function afterRender(result: LayoutResult): void {
    stopInteractions?.();
    stopInteractions = null;
    if (opts.interactions !== false) {
      stopInteractions = enableChartInteractions(stage, typeof opts.interactions === "object" ? opts.interactions : {});
    }
    applyZoom();
    setPage(page, false);
    syncBar();
    opts.onLayout?.(result);
  }

  const api: ReportViewer = {
    result: null,
    page: 1,
    pageCount: 0,
    zoom: 1,
    async update(input) {
      opts = { ...opts, ...input };
      const result = await preview.update({ ...input, mode: "preview", zoom: 1 });
      if (destroyed) return result;
      afterRender(result);
      return result;
    },
    goToPage(n) {
      setPage(n, true);
    },
    setZoom(value) {
      if (typeof value === "number") opts = { ...opts, zoom: value };
      else opts = { ...opts, fit: value, zoom: undefined };
      applyZoom();
    },
    print() {
      const result = preview.result;
      if (!result) return;
      if (opts.onPrint) opts.onPrint(result);
      else win?.print();
    },
    destroy() {
      destroyed = true;
      observer?.disconnect();
      scroll.removeEventListener("scroll", onScroll);
      scroll.removeEventListener("keydown", onKey);
      stopInteractions?.();
      preview.destroy();
      root.remove();
    }
  };

  // `result`, `page`, `pageCount` and `zoom` read through to live state rather
  // than being copies a caller could find stale one frame after a scroll.
  Object.defineProperties(api, {
    result: { get: () => preview.result, enumerable: true },
    page: { get: () => page, enumerable: true },
    pageCount: { get: () => preview.result?.paged.totalPages ?? 0, enumerable: true },
    zoom: { get: () => zoom, enumerable: true }
  });

  syncBar();
  void api.update({});
  return api;
}
